import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useUpdateAsset } from '@/queries/assets/update-asset';
import { getListOrganizationsOption } from '@/queries/organizations/list-organizations';
import { SelectOrganizationPopover } from '../../Organizations/SelectOrganizationPopup';

type MoveAssetDialogProps = {
	isOpen: boolean;
	onClose: () => void;
	assetId: string;
	currentOrgId: string;
	assetTitle: string;
	onMoveSuccess: () => void;
};

export function MoveAssetDialog({
	isOpen,
	onClose,
	assetId,
	onMoveSuccess,
	currentOrgId,
	assetTitle,
}: MoveAssetDialogProps) {
	const { t } = useTranslation();
	const [targetOrgId, setTargetOrgId] = useState<string>('');

	// Use moveMutation logic
	const updateAssetApi = useMutation(useUpdateAsset());

	// Fetch available organizations
	const { data: orgListResponse, isLoading: isLoadingOrgs } = useQuery(getListOrganizationsOption({ enabled: isOpen }));

	// Inactive organizations are read-only, so assets cannot be moved into them
	const availableOrgs =
		orgListResponse?.organizations.filter((org) => org.id !== currentOrgId && org.enabled !== false) || [];

	const handleMove = async () => {
		if (!targetOrgId) return;
		// Trigger the dedicated move mutation
		await updateAssetApi.mutateAsync({ id: assetId, assetPayload: { organizationId: targetOrgId } });
		onMoveSuccess();
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-106.25">
				<DialogHeader>
					<DialogTitle>{t('assets.form.moveAsset')}</DialogTitle>
					<DialogDescription>
						{t('assets.form.moveDescriptionPrefix')} <strong>{assetTitle}</strong>{' '}
						{t('assets.form.moveDescriptionSuffix')}
					</DialogDescription>
				</DialogHeader>

				<div className="grid gap-4 py-4">
					<div className="flex flex-col gap-2">
						<label htmlFor="select-organizations" className="text-sm font-medium">
							{t('assets.form.selectTargetOrganization')}
						</label>
						{isLoadingOrgs && (
							<div className="flex items-center gap-2 text-sm py-2">
								<Spinner /> {t('assets.form.loadingOrganizations')}
							</div>
						)}
						{!isLoadingOrgs && !availableOrgs.length && (
							<div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
								{t('assets.form.noOtherOrganizations')}
							</div>
						)}
						{!isLoadingOrgs && !!availableOrgs.length && (
							<SelectOrganizationPopover
								organizations={availableOrgs}
								value={targetOrgId}
								onChange={(value) => setTargetOrgId(value)}
							/>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={updateAssetApi.isPending}>
						{t('common.actions.cancel')}
					</Button>
					<Button onClick={handleMove} disabled={!targetOrgId || updateAssetApi.isPending} variant="default">
						{updateAssetApi.isPending && <Spinner className="mr-2 size-4" />}
						<ArrowRightLeft className="mr-2 size-4" />
						{t('assets.form.moveAsset')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
